import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Avatar,
  Checkbox,
  Box,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import DataStatusAlert from "../../../Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import { useDispatch, useSelector } from "react-redux";
import {
  setSort,
  setPage,
  setSelectedRows,
  setRowsPerPage,
  setOpenModalType,
} from "../../../../../features/businessInsightsSlice";
import { useEffect } from "react";
import store from "../../../../../store";
import ResolveInsightsPopup from "../InsightsTableActions/ResolveInsights/ResolveInsightsPopup";
import AssignInsightsPopup from "../InsightsTableActions/AssignInsights/AssignInsightsPopup";
import SnoozeInsightsPopup from "../InsightsTableActions/SnoozeInsights/SnoozeInsightsPopup";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import { formatDate } from '../../../../../utils/helpers';

const InsightsTable = ({ loading }: any) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = React.useState(true);
  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const filteredInsights = useSelector(
    (state: any) => state.businessInsights?.filteredInsights
  );
  const selectedBusinessInsight = useSelector(
    (state: any) => state.businessInsights?.selectedBusinessInsight
  );
  const selectedCategory = useSelector(
    (state: any) => state.businessInsights?.selectedCategory
  );
  const selectedSubCategory = useSelector(
    (state: any) => state.businessInsights?.selectedSubCategory
  );
  const selectedStatus = useSelector(
    (state: any) => state.businessInsights?.selectedStatus
  );
  const createdOnDate = useSelector(
    (state: any) => state.businessInsights?.createdOnDate
  );
  const selectedDueDate = useSelector(
    (state: any) => state.businessInsights?.selectedDueDate
  );
  const selectedAssignee = useSelector(
    (state: any) => state.businessInsights?.selectedAssignee
  );
  const selectedRows = useSelector(
    (state: any) => state.businessInsights?.selectedRows
  );
  const sort = useSelector((state: any) => state.businessInsights?.sort);
  const rowsPerPage = useSelector(
    (state: any) => state.businessInsights?.rowsPerPage
  );
  const page = useSelector((state: any) => state.businessInsights?.page);
  const openModalType = useSelector(
    (state: any) => state.businessInsights?.openModalType
  );
  const categoriesToKeep = ["User Acquisition", "Player Value"]; // Define the categories to keep
  const requiredInsights = filteredInsights?.filter((task: any) => {
    const categody =
      task?.data &&
      task?.data["query_output:category"] &&
      task?.data["query_output:category"].value;
    return categoriesToKeep.includes(categody);
  });

  const filteredData = sortedData.filter((items: any) => {
    const selectedBusinessInsightName =
      selectedBusinessInsight && selectedBusinessInsight.length !== 0
        ? selectedBusinessInsight.includes(
            items?.data["query_output:sec_title"]?.value
          )
        : true;

    const selectedCategoryName =
      selectedCategory && selectedCategory.length !== 0
        ? selectedCategory.includes(
            items?.data?.["query_output:category"]?.value
          )
        : true;

    const selectedSubCategoryName =
      selectedSubCategory && selectedSubCategory.length !== 0
        ? selectedSubCategory.includes(
            items?.data?.["query_output:subcategory"]?.value
          )
        : true;

    const selectedStatusName =
      selectedStatus && selectedStatus.length !== 0
        ? selectedStatus.includes(items?.current_status)
        : true;

    const selectedAssigneeName =
      selectedAssignee && selectedAssignee.length !== 0
        ? selectedAssignee.includes(items?.assignee_fullname)
        : true;

    const selectedCreatedDateRange =
      createdOnDate &&
      createdOnDate.length === 2 &&
      createdOnDate[0] &&
      createdOnDate[1]
        ? createdOnDate[0] === createdOnDate[1]
          ? new Date(items?.created_at).toDateString() ===
            new Date(createdOnDate[0]).toDateString()
          : new Date(items?.created_at) >= new Date(createdOnDate[0]) &&
            new Date(items?.created_at) <=
              new Date(new Date(createdOnDate[1]).setHours(23, 59, 59, 999))
        : true;

    const selectedDueDateRange =
      selectedDueDate &&
      selectedDueDate.length === 2 &&
      selectedDueDate[0] &&
      selectedDueDate[1]
        ? selectedDueDate[0] === selectedDueDate[1]
          ? new Date(items?.due_date).toDateString() ===
            new Date(selectedDueDate[0]).toDateString()
          : new Date(items?.due_date) >= new Date(selectedDueDate[0]) &&
            new Date(items?.due_date) <=
              new Date(new Date(selectedDueDate[1]).setHours(23, 59, 59, 999))
        : true;

    return (
      selectedBusinessInsightName &&
      selectedCategoryName &&
      selectedSubCategoryName &&
      selectedStatusName &&
      selectedAssigneeName &&
      selectedCreatedDateRange &&
      selectedDueDateRange
    );
  });

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        // Default sorting by modified_at (To make new items appear first in your table when they are Created or Added)
        return arrayToSort;
      }
      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;
        if (keytoSort === undefined) {
          return 0;
        }
        let aValue, bValue;

        // Handle nested keys (e.g., "Category" or "Target")
        if (keytoSort === "query_output:sec_title") {
          aValue = a.data?.["query_output:sec_title"]?.value;
          bValue = b.data?.["query_output:sec_title"]?.value;
        } else if (keytoSort === "query_output:category") {
          aValue = a.data?.["query_output:category"]?.value;
          bValue = b.data?.["query_output:category"]?.value;
        } else if (keytoSort === "query_output:subcategory") {
          aValue = a.data["query_output:subcategory"]?.value;
          bValue = b.data["query_output:subcategory"]?.value;
        } else if (keytoSort === "target") {
          aValue = a.data["target"];
          bValue = b.data["target"];
        }
        // Handle "Status Action Date" logic
        else if (keytoSort === "status_action_date") {
          // Define your desired status priority (lower index = higher priority)
          type StatusKey = "In Progress" | "To Do" | "Snooze" | "Done";
          const statusPriority: Record<StatusKey, number> = {
            "In Progress": 1,
            "To Do": 2,
            Snooze: 3,
            Done: 4,
            // Add more statuses as needed
          };

          const aStatus = a.current_status || "";
          const bStatus = b.current_status || "";

          const aPriority = statusPriority[aStatus as StatusKey] ?? 99;
          const bPriority = statusPriority[bStatus as StatusKey] ?? 99;

          // First, sort by status priority
          if (aPriority !== bPriority) {
            return sort.direction === "asc"
              ? aPriority - bPriority
              : bPriority - aPriority;
          }

          // If same status, sort by date
          aValue = a.snooze_end || a.status_log[0]?.modified_at;
          bValue = b.snooze_end || b.status_log[0]?.modified_at;

          // Fallback to date comparison
          if (aValue && bValue) {
            const aDate = new Date(aValue).getTime();
            const bDate = new Date(bValue).getTime();
            return sort.direction === "asc" ? aDate - bDate : bDate - aDate;
          }
          if (aValue) return -1;
          if (bValue) return 1;
          return 0;
        }
        // Default case for other columns
        else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  React.useEffect(() => {
    if (filteredInsights) {
      const sortedRows = getSortedData(requiredInsights);
      setSortedData(sortedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredInsights,
    getSortedData,
    selectedAssignee,
    selectedCategory,
    selectedAssignee,
    selectedBusinessInsight,
    selectedDueDate,
    selectedStatus,
  ]);

  const tableheaders = [
    {
      id: 1,
      KEY: "query_output:sec_title",
      label: "Insight Name",
      sortable: true,
    },
    { id: 2, KEY: "query_output:category", label: "Category", sortable: true },
    {
      id: 3,
      KEY: "query_output:subcategory",
      label: "Insight Type",
      sortable: true,
    },
    { id: 4, KEY: "current_status", label: "Status", sortable: true },
    {
      id: 5,
      KEY: "status_action_date",
      label: "Status Action Date",
      sortable: true,
    },
    { id: 6, KEY: "due_date", label: "Due Date", sortable: true },
    { id: 7, KEY: "assignee_fullname", label: "Assignee", sortable: true },
    { id: 8, KEY: "created_at", label: "Created On", sortable: true },
  ];

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(
      setSort({
        keytoSort: header.KEY,
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    ); // Toggle sort direction if the same column is clicked, otherwise default to ascending
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));

    // Update the URL param when page changes
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  const handleSelectRow = (row: number) => {
    const updatedSelectedRows = selectedRows.includes(row)
      ? selectedRows.filter((i: any) => i !== row) // Remove if already selected
      : [...selectedRows, row]; // Add if not selected
    store.dispatch(setSelectedRows(updatedSelectedRows));
  };

  useEffect(() => {
    // Get current page from URL searchParams if it exists
    const currentPage = searchParams.get("page");

    // If page exists in URL, use that value (convert to number)
    if (currentPage !== null) {
      dispatch(setPage(Number(currentPage)));
    }
    // Otherwise reset to page 0 (when filters change)
    else {
      dispatch(setPage(0));
      // Also update URL to reflect page 0
      setSearchParams((prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);
        updatedParams.set("page", "0");
        return updatedParams;
      });
    }
    // eslint-disable-next-line
  }, [
    selectedAssignee,
    selectedCategory,
    selectedAssignee,
    selectedBusinessInsight,
    selectedDueDate,
    selectedStatus,
    searchParams,
  ]);

  //-- Function to close modal(snooze/assign/resolve)
  const handleCloseModal = () => {
    dispatch(setOpenModalType(null));
  };

  type StatusType = any;

  const statusConfigs: Record<StatusType, (row: any) => string> = {
    Snooze: (row: any) => `Until ${formatDate(row?.snooze_end) || ""}`,
    "In Progress": (row: any) =>
      `Since ${
        formatDate(row?.status_log?.[row.status_log.length - 1]?.modified_at) ||
        ""
      }`,
    Done: (row: any) =>
      `On ${
        formatDate(row?.status_log?.[row.status_log.length - 1]?.modified_at) ||
        ""
      }`,
    "To Do": (row: any) =>
      `On ${
        formatDate(row?.status_log?.[row.status_log.length - 1]?.modified_at) ||
        ""
      }`,
  };

  const getStatusMessage = (row: any) => {
    const status = row?.current_status as any;
    const statusFn = status ? statusConfigs[status] : "";
    return statusFn
      ? statusFn(row)
      : `On ${
          formatDate(
            row?.status_log?.[row.status_log.length - 1]?.modified_at
          ) || ""
        }`;
  };

  useEffect(() => {
    dispatch(setSelectedRows([]));
    // eslint-disable-next-line
  }, []);

  const selectedSites = searchParams.get("selectedSites");
  const search = searchParams.get("search");
  const lab1TaskSharedUid = "39caae988f684bbca8aa8c6fd7924ecf";
  const lab2TaskSharedUid = "210190e5ca7b4e29a83e8537b4c92d4e";
  const lab3TaskSharedUid = "dcb12de5b0e54e47af190902e42fac09";
  const lab4TaskSharedUid = "4bfc5e1b714c4b88b186f2f4042244e8";

  const ROUTE_MAPPING: { [key: string]: string } = {
    [lab1TaskSharedUid]: "/performance/insights/Adgroup",
    [lab2TaskSharedUid]: "/performance/insights/Campaign",
    [lab3TaskSharedUid]: "/performance/insights/Adgroup",
    [lab4TaskSharedUid]: "/performance/insights/Campaign",
  };

  useEffect(() => {
    if (filteredInsights) {
      setIsLoading(false);
      const sortedRows = getSortedData(requiredInsights);
      setSortedData(sortedRows);
    } else {
      setIsLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading && filteredInsights?.length <= 0 ? (
        <SimpleBackdrop />
      ) : (
        <>
          <TableContainer className="" component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableheaders.map((header) => (
                    <TableCell key={header.id} align="left">
                      <TableSortLabel
                        active={sort.keytoSort === header.KEY}
                        direction={
                          sort.keytoSort === header.KEY ? sort.direction : "asc"
                        }
                        onClick={() => handleHeaderClick(header)}
                        disabled={!header.sortable}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body1" color="textSecondary">
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredData?.length > 0 ? (
                  <>
                    {filteredData
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((row: any, index: any) => (
                        <React.Fragment key={index}>
                          <TableRow
                            key={index}
                            selected={selectedRows.includes(index)}
                            sx={{
                              "& > *": {
                                borderBottom:
                                  "1px solid rgba(224, 224, 224, 1) !important",
                              },
                            }}
                          >
                            <TableCell align="left" data-tracker="304">
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  color="secondary"
                                  checked={selectedRows.includes(row)}
                                  onChange={() => handleSelectRow(row)}
                                  sx={{
                                    paddingLeft: 0,
                                    marginLeft: 0,
                                    marginRight: 2,
                                  }}
                                />
                                <RouterLink
                                  to={`${
                                    ROUTE_MAPPING[
                                      row?.task_structure_shared_uid
                                    ] ||
                                    "/performance/insights/Adgroup"
                                  }?${(() => {
                                    const params = new URLSearchParams();
                                    params.set(
                                      "taskStructureUid",
                                      row?.task_structure_shared_uid
                                    );
                                    params.set(
                                      "taskSharedUid",
                                      row?.shared_uid
                                    );
                                    params.set(
                                      "selectedSites",
                                      selectedSites || ""
                                    );
                                    search
                                      ? params.set("search", search)
                                      : params.delete("search");
                                    const isAdgroupType = [
                                      lab1TaskSharedUid,
                                      lab3TaskSharedUid,
                                    ].includes(row?.task_structure_shared_uid); // Determine insightType based on task_structure_shared_uid
                                    params.set(
                                      "insightType",
                                      isAdgroupType ? "adgroup" : "campaign"
                                    );
                                    return params.toString();
                                  })()}`}
                                  style={{
                                    textDecoration: "underline",
                                    color: "#9C27B0",
                                  }}
                                  data-tracker="304"
                                >
                                  {row?.data?.["query_output:sec_title"]
                                    ?.value || "N/A"}
                                </RouterLink>
                              </Box>
                            </TableCell>
                            <TableCell align="left">
                              {row?.data?.["query_output:category"]?.value ||
                                "N/A"}
                            </TableCell>
                            <TableCell align="left">
                              {row?.data?.["query_output:subcategory"]?.value ||
                                "N/A"}
                            </TableCell>
                            <TableCell align="left">
                              <DataStatusAlert
                                status={row?.current_status || "N/A"}
                              />
                            </TableCell>
                            <TableCell align="left">
                              {getStatusMessage(row)}
                            </TableCell>
                            <TableCell align="left">
                              {row?.due_date || ""}
                            </TableCell>
                            <TableCell align="left">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <Avatar
                                  sx={{ width: 22, height: 22, fontSize: 12 }}
                                >
                                  {row?.assignee_fullname
                                    ?.charAt(0)
                                    ?.toUpperCase()}
                                </Avatar>
                                {row?.assignee_fullname || "N/A"}
                              </div>
                            </TableCell>
                            <TableCell align="left">
                              {row?.created_at
                                ? formatDate(row?.created_at)
                                : ""}
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body1" color="textSecondary">
                        No Data Available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Stack
              spacing={1}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={filteredData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },
                  ".MuiTablePagination-actions": {
                    display: "none",
                  },
                  ".MuiTablePagination-toolbar": {
                    paddingLeft: "16px",
                  },
                }}
              />
              <Pagination
                count={Math.ceil(filteredData?.length / rowsPerPage)}
                page={page + 1}
                showFirstButton
                showLastButton
                className="pagination-button"
                color="secondary"
                size="small"
                sx={{
                  ".css-wjh20t-MuiPagination-ul": {
                    justifyContent: "center",
                  },
                }}
                onChange={(_, value) => handleChangePage(null, value - 1)}
              />
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                classes={{
                  displayedRows: "hidden-displayed-rows", // Apply the custom class
                }}
                sx={{
                  ".MuiTablePagination-actions": {
                    display: "none",
                  },
                  ".MuiTablePagination-input": {
                    marginRight: "15px",
                  },
                }}
              />
            </Stack>
          </TableContainer>
        </>
      )}
      {openModalType === "resolve" && (
        <ResolveInsightsPopup open={true} handleClose={handleCloseModal} />
      )}
      {openModalType === "assign" && (
        <AssignInsightsPopup open={true} handleClose={handleCloseModal} />
      )}
      {openModalType === "snooze" && (
        <SnoozeInsightsPopup open={true} handleClose={handleCloseModal} />
      )}
    </>
  );
};

export default InsightsTable;
